import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import 'rxjs/Rx';
import { catchError } from 'rxjs/operators/catchError';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthorizationProperties } from 'src/app/authentication/auth-info';
import { LoginService } from './login.service';
import { Incident } from 'src/app/model/incident';
import { UrlMaker } from 'src/app/commons/url-maker';
import { OperationResult } from '@tb/auth';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { EmailValidation } from 'src/app/model/EmailValidation';

@Injectable()
export class ModelService {
    industriesServiceURL: string;
    authorizationStored;
    public authorizationProperties: AuthorizationProperties;
    storeApiUrl: string;

    constructor(private http: HttpClient, private loginService: LoginService) {
        this.authorizationStored = this.loginService.getauthinfo();
        this.authorizationProperties = JSON.parse(this.authorizationStored);
        this.http = http;
        this.industriesServiceURL = environment.settings.industriesServiceUrl;
        this.storeApiUrl = environment.settings.storeApiUrl;
    }

    // --------------------------------------------------------------------------------
    //[HttpPost("/api/accountsubscriptions/{accountName}/{subscriptionKey}")]
    addAccountSubscriptionAssociation(accountName: string, subscriptionKey: string, expirationdate: Date): Observable<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let expDate: string = '';

        if (expirationdate) expDate = JSON.stringify(new Date(expirationdate).toISOString());

        const urlMaker = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('accountSubscriptions')
            .appendSegment(accountName)
            .appendSegment(subscriptionKey);

        return this.http
            .post<OperationResult>(urlMaker.toString(), expDate, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in addAccountSubscriptionAssociation')));
    }

    // --------------------------------------------------------------------------------
    // [HttpGet("/api/accounthomeinfo/{accountName}")]
    public async getAccountHomeInfoToPromise(parentAccountName: string): Promise<OperationResult> {
        return this.getAccountHomeInfo(parentAccountName).toPromise();
    }

    // --------------------------------------------------------------------------------
    // [HttpGet("/api/accounthomeinfo/{accountName}")]
    getAccountHomeInfo(parentAccountName: string): Observable<OperationResult> {
        if (parentAccountName === undefined || parentAccountName == '') return throwError('parentAccountName is missing!');

        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('accounthomeinfo')
            .appendSegment(parentAccountName)
            .toString();

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return this.http.get<OperationResult>(url, { headers: headers }).pipe(
            catchError((error: HttpErrorResponse) => {
                let errorDetail = error.message;

                if (error.status === 401) {
                    return throwError('Unauthorized: ' + errorDetail);
                }
                if (error.status === 403) {
                    return throwError('Forbidden resource: ' + errorDetail);
                }

                return throwError(errorDetail || 'server error (getAccountHomeInfo)');
            })
        );
    }

    // --------------------------------------------------------------------------------
    //[HttpGet("api/getsymbols/")]
    public async getSymbolsToPromise(): Promise<OperationResult> {
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('getsymbols').toString();
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.get<OperationResult>(url, { headers: headers }).toPromise();
    }

    // --------------------------------------------------------------------------------
    //[HttpGet("/api/subaccroles/{AccountName}/{subscriptionKey}/{accOrSub}")]
    getAccountsAssociatedToPromiseTo(accountName: string, subKey: string, roleLevel: string): Promise<OperationResult> {
        if (accountName === undefined || accountName == '') {
            alert('accountName is missing!');
            return;
        }
        if (subKey === undefined || subKey == '') {
            alert('subKey is missing!');
            return;
        }
        if (roleLevel === undefined || roleLevel == '') {
            alert('roleLevel is missing!');
            return;
        }

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('subaccroles')
            .appendSegment(accountName)
            .appendSegment(subKey)
            .appendSegment(roleLevel)
            .toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    let errorDetail = error.message;

                    if (error.status === 401) {
                        return throwError('Unauthorized: ' + errorDetail);
                    }
                    if (error.status === 403) {
                        return throwError('Forbidden resource: ' + errorDetail);
                    }

                    return throwError(errorDetail || 'server error (getAccountsAssociatedTo)');
                })
            )
            .toPromise();
    }

    // --------------------------------------------------------------------------------
    //[HttpDelete("/api/accountsubscriptions/{accountName}/{subscriptionKey}")]
    deleteAccountSubscriptionAssociation(accountName: string, subscriptionKey: string): Observable<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const urlMaker = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('accountsubscriptions')
            .appendSegment(accountName)
            .appendSegment(subscriptionKey);
        // const url = this.storeApiUrl + 'accountsubscriptions/' + accountName + '/' + subscriptionKey;

        return this.http
            .delete<OperationResult>(urlMaker.toString(), { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in deleteAccountSubscriptionAssociation')));
    }

    // --------------------------------------------------------------------------------
    //[HttpPost("/api/emails")]
    saveMpEmail(body: EmailValidation): Observable<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const urlMaker = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('emails')
            .appendSegment(body.SubscriptionKey)
            .toString();

        return this.http
            .post<OperationResult>(urlMaker, body, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in saveMpEmail')));
    }

    // --------------------------------------------------------------------------------
    //[HttpGet("/api/emails/{subscriptionKey}")]
    getSubsEmailToPromise(subscriptionKey: string): Promise<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const urlMaker = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('emails').appendSegment(subscriptionKey);

        return this.http
            .get<OperationResult>(urlMaker.toString(), { headers: headers })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    let errorDetail = error.message;
                    if (error.status === 401) {
                        return throwError('Unauthorized: ' + errorDetail);
                    }
                    if (error.status === 403) {
                        return throwError('Forbidden resource: ' + errorDetail);
                    }
                    return throwError(errorDetail || 'server error (getSubsEmailToPromise)');
                })
            )
            .toPromise();
    }

    // --------------------------------------------------------------------------------
    //[HttpGet("/api/isemailvalidated/{mailaddress}/{subKey}")]
    getEmailIsValidated(accountName: string, subKey: string): Observable<OperationResult> {
        const urlMaker = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('isemailvalidated')
            .appendSegment(accountName)
            .appendSegment(subKey);

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return this.http
            .get<OperationResult>(urlMaker.toString(), { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in getEmailIsValidated')));
    }

    // --------------------------------------------------------------------------------
    //[HttpGet("api/getlanguagesdescription/{accountName}")]
    getLanguages(accountName?: string): Observable<OperationResult> {
        const urlMaker = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('getlanguagesdescription')
            .appendSegment(accountName)
            .toString();

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return this.http
            .get<OperationResult>(urlMaker, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in getLanguages')));
    }

    // --------------------------------------------------------------------------------
    sendForm(body: Incident): Observable<OperationResult> {
        let urlMaker = new UrlMaker();
        urlMaker.appendBaseUrl(this.storeApiUrl).appendSegment('sendForm');
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return this.http
            .post<OperationResult>(urlMaker.toString(), body, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in sendForm')));
    }

    // --------------------------------------------------------------------------------
    incidentUploadFile(attachment: File): Observable<OperationResult> {
        let urlMaker = new UrlMaker();
        urlMaker.appendBaseUrl(this.storeApiUrl).appendSegment('incidents').appendSegment('attachment');

        const uploadData = new FormData();
        uploadData.append('attachment', attachment, attachment.name);
        return this.http
            .post<OperationResult>(urlMaker.toString(), uploadData)
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in incidentUploadFile')));
    }

    // --------------------------------------------------------------------------------
    public async getIndustryPurchasedDetails(accountName: string, subscriptionKey: string): Promise<OperationResult> {
        let url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('industrydetails')
            .appendSegment(accountName)
            .appendSegment(subscriptionKey)
            .toString();

        return this.http.get<OperationResult>(url).toPromise();
    }

    // --------------------------------------------------------------------------------
    TestConnectAndGetUserLogins(subKey: string): Observable<OperationResult> {
        if (subKey === '') {
            return throwError('The subKey to query is missing!');
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('testdbandusers')
            .appendSegment(subKey)
            .toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => ErrorObservable.create(error || 'Server error')));
    }

    public async getRankingInfo(instanceKey: string, accountName: string, culture: string): Promise<OperationResult> {
        let url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('rankingInfo')
            .appendSegment(instanceKey)
            .appendSegment(accountName)
            .appendSegment(culture)
            .toString();

        return this.http.get<OperationResult>(url).toPromise();
    }

    public async getCustomerInfo(customerId: string, accountName: string): Promise<OperationResult> {
        let url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('customerInfo')
            .appendSegment(customerId)
            .appendSegment(accountName)
            .toString();

        return this.http.get<OperationResult>(url).toPromise();
    }
}
